import React, { useEffect, useState } from 'react';
import _ from 'underscore';
import { v4 as uuid } from 'uuid';
import { Button, Checkbox, Form, Icon, Input, Label, List, Modal, TextArea } from 'semantic-ui-react';

import { EmptyCollectionMessage } from '@src/views/components/util/messaging/EmptyCollectionMessage';
import { FinePrintMessage }       from '@src/views/components/util/messaging/FinePrintMessage';
import { LoadingIndicator }       from '@src/views/components/util/loading/LoadingIndicator';
import { useCentralCalls }        from '@src/rest/CentralCalls';


// --- Types ---

export type SurveyQuestionInputType = 'DROPDOWN' | 'MULTISELECT' | 'TEXT' | 'NUMBER';
export type SurveyQuestionCategory  = 'CORPORATE' | 'MARKETING' | 'MANUFACTURING' | 'SALES';

// UI model for a survey question. Field names are the component's own; the mapping to/from
// the database (PascalCase columns) happens in the data-access layer below.
export interface SurveyQuestion {
    id:              string;
    questionText:    string;
    description:     string;
    type:            SurveyQuestionInputType;
    category:        SurveyQuestionCategory;
    options:         string[];
    hasFollowUpText: boolean;
    isRequired:      boolean;
}


// --- Constants ---

const CATEGORY_COLOR: Record<SurveyQuestionCategory, string> = {
    CORPORATE:     'purple',
    MARKETING:     'pink',
    MANUFACTURING: 'olive',
    SALES:         'blue',
};

const CATEGORY_LABEL: Record<SurveyQuestionCategory, string> = {
    CORPORATE:     'Corporate',
    MARKETING:     'Marketing',
    MANUFACTURING: 'Manufacturing',
    SALES:         'Sales',
};

function getTypeBadgeLabel( question: SurveyQuestion ): string {
    switch( question.type ){
        case 'DROPDOWN':
        case 'MULTISELECT':
            return question.hasFollowUpText ? 'SELECTION + TEXT' : 'SELECTION';
        case 'TEXT':
            return 'FREE TEXT';
        case 'NUMBER':
            return 'NUMBER';
    }
}

const newId = () => uuid();


// --- REST data access ---
// TableName values must match the DbSet property names on CorvalContext (plural).
const SURVEY_QUESTION_TABLE = 'SurveyQuestions';
const SURVEY_QUESTION_OPTION_TABLE = 'SurveyQuestionOptions';

// Shapes returned by the generic TableSelect endpoint. Although the DB columns are PascalCase,
// this endpoint serializes responses as camelCase (see the "results come out lowercase fields"
// note in AIObjectPicker.tsx), so reads use camelCase. Writes below stay PascalCase — the
// backend matches those column names case-insensitively.
interface SurveyQuestionRow {
    id:           string;
    questionType: string;
    question:     string;
    description:  string | null;
}

interface SurveyQuestionOptionRow {
    id:               string;
    surveyQuestionId: string;
    optionText:       string;
}

function toLocalQuestion( row: SurveyQuestionRow, optionRows: SurveyQuestionOptionRow[] ): SurveyQuestion {
    return {
        id:              row.id,
        questionText:    row.question,
        description:     row.description ?? '',
        type:            ( row.questionType as SurveyQuestionInputType ) ?? 'TEXT',
        // category, isRequired, hasFollowUpText are not yet persisted — default them
        category:        'CORPORATE',
        options:         optionRows.map( o => o.optionText ),
        hasFollowUpText: false,
        isRequired:      false,
    };
}


// --- Edit Question Modal ---

interface EditQuestionModalProps {
    question: SurveyQuestion | null;
    open:     boolean;
    onClose:  () => void;
    onSave:   ( question: SurveyQuestion ) => void;
}

const BLANK_QUESTION: Omit<SurveyQuestion, 'id'> = {
    questionText:    '',
    description:     '',
    type:            'DROPDOWN',
    category:        'CORPORATE',
    options:         [ '' ],
    hasFollowUpText: false,
    isRequired:      false,
};

function EditQuestionModal( { question, open, onClose, onSave }: EditQuestionModalProps ){

    const [ draft, setDraft ] = useState<SurveyQuestion>( question ?? { ...BLANK_QUESTION, id: newId() } );

    React.useEffect( () => {
        setDraft( question ? { ...question } : { ...BLANK_QUESTION, id: newId() } );
    }, [ question, open ] );

    const hasOptions = draft.type === 'DROPDOWN' || draft.type === 'MULTISELECT';

    const setField = <K extends keyof SurveyQuestion>( key: K, value: SurveyQuestion[ K ] ) => {
        setDraft( prev => ( { ...prev, [ key ]: value } ) );
    };

    const updateOption = ( index: number, value: string ) => {
        const updated = [ ...draft.options ];
        updated[ index ] = value;
        setField( 'options', updated );
    };

    const addOption = () => setField( 'options', [ ...draft.options, '' ] );

    const copyOption = ( index: number ) => {
        const updated = [ ...draft.options ];
        updated.splice( index + 1, 0, draft.options[ index ] );
        setField( 'options', updated );
    };

    const removeOption = ( index: number ) => {
        setField( 'options', draft.options.filter( ( _, i ) => i !== index ) );
    };

    const TYPE_BUTTONS: { type: SurveyQuestionInputType; label: string }[] = [
        { type: 'TEXT', label: 'Text' },
        { type: 'DROPDOWN', label: 'Dropdown' },
        { type: 'MULTISELECT', label: 'Multi-select' },
        { type: 'NUMBER', label: 'Number' },
    ];

    const CATEGORY_OPTIONS: { value: SurveyQuestionCategory; label: string }[] = [
        { value: 'CORPORATE', label: 'Corporate' },
        { value: 'MARKETING', label: 'Marketing' },
        { value: 'MANUFACTURING', label: 'Manufacturing' },
        { value: 'SALES', label: 'Sales' },
    ];

    return (
        <Modal open={ open } onClose={ onClose } size="small">

            <Modal.Header style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }>
                <span>{ question ? 'Edit Question' : 'Add Question' }</span>
                <Icon
                    name="times"
                    style={ { cursor: 'pointer', color: '#aaa', fontSize: '1rem', margin: 0 } }
                    onClick={ onClose }
                />
            </Modal.Header>

            <Modal.Content scrolling>
                <Form>

                    {/* --- Question Type --- */}
                    <Form.Field>
                        <label>Question Type</label>
                        <Button.Group fluid>
                            { TYPE_BUTTONS.map( btn => (
                                <Button
                                    key={ btn.type }
                                    type="button"
                                    basic={ draft.type !== btn.type }
                                    color={ draft.type === btn.type ? 'purple' : undefined }
                                    onClick={ () => setField( 'type', btn.type ) }
                                    style={ { padding: '10px 6px' } }>
                                    <div style={ { textAlign: 'center' } }>
                                        { btn.type === 'TEXT' && (
                                            <strong style={ { display: 'block', fontSize: '1.1em', marginBottom: '3px' } }>T</strong>
                                        ) }
                                        { btn.type === 'DROPDOWN' && (
                                            <Icon name="chevron down" style={ { display: 'block', margin: '0 auto 3px' } }/>
                                        ) }
                                        { btn.type === 'MULTISELECT' && (
                                            <Icon name="check square outline" style={ { display: 'block', margin: '0 auto 3px' } }/>
                                        ) }
                                        { btn.type === 'NUMBER' && (
                                            <strong style={ { display: 'block', fontSize: '0.85em', marginBottom: '3px' } }>123</strong>
                                        ) }
                                        <span style={ { fontSize: '0.75em' } }>{ btn.label }</span>
                                    </div>
                                </Button>
                            ) ) }
                        </Button.Group>
                    </Form.Field>

                    {/* --- Category --- */}
                    <Form.Field>
                        <label>Category</label>
                        <Button.Group>
                            { CATEGORY_OPTIONS.map( opt => (
                                <Button
                                    key={ opt.value }
                                    type="button"
                                    size="small"
                                    basic={ draft.category !== opt.value }
                                    color={ draft.category === opt.value ? ( CATEGORY_COLOR[ opt.value ] as any ) : undefined }
                                    onClick={ () => setField( 'category', opt.value ) }>
                                    { opt.label }
                                </Button>
                            ) ) }
                        </Button.Group>
                    </Form.Field>

                    {/* --- Question Text --- */}
                    <Form.Field>
                        <label>Question</label>
                        <TextArea
                            rows={ 3 }
                            value={ draft.questionText }
                            maxLength={ 255 }
                            placeholder="Enter your question..."
                            onChange={ ( _, d ) => setField( 'questionText', d.value as string ) }
                        />
                        <div style={ { textAlign: 'right', fontSize: '0.75em', color: '#999', marginTop: '4px' } }>
                            { draft.questionText.length } / 255
                        </div>
                    </Form.Field>

                    {/* --- Description --- */}
                    <Form.Field>
                        <label>
                            Description{ ' ' }
                            <span style={ { fontWeight: 'normal', color: '#999', fontSize: '0.9em' } }>(Optional)</span>
                        </label>
                        <TextArea
                            rows={ 2 }
                            value={ draft.description }
                            maxLength={ 500 }
                            placeholder="Add additional context for the respondent..."
                            onChange={ ( _, d ) => setField( 'description', d.value as string ) }
                        />
                        <div style={ { textAlign: 'right', fontSize: '0.75em', color: '#999', marginTop: '4px' } }>
                            { draft.description.length } / 500
                        </div>
                    </Form.Field>

                    {/* --- Answer Options --- */}
                    { hasOptions && (
                        <Form.Field>
                            <label>Answer Options</label>
                            <List>
                                { draft.options.map( ( option, index ) => (
                                    <List.Item
                                        key={ index }
                                        style={ { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' } }>
                                        <Icon name="bars" style={ { color: '#ccc', cursor: 'grab', flexShrink: 0 } }/>
                                        <Input
                                            fluid
                                            size="small"
                                            value={ option }
                                            placeholder={ `Option ${ index + 1 }` }
                                            onChange={ e => updateOption( index, e.target.value ) }
                                            style={ { flex: 1 } }
                                        />
                                        <Icon
                                            name="copy outline"
                                            link
                                            style={ { color: '#888', flexShrink: 0 } }
                                            onClick={ () => copyOption( index ) }
                                        />
                                        <Icon
                                            name="trash alternate outline"
                                            link
                                            style={ { color: '#e57373', flexShrink: 0 } }
                                            onClick={ () => removeOption( index ) }
                                        />
                                    </List.Item>
                                ) ) }
                            </List>
                            <Button type="button" basic size="small" onClick={ addOption }>
                                <Icon name="plus"/> Add Option
                            </Button>
                        </Form.Field>
                    ) }

                    {/* --- Follow-up Text Toggle (DROPDOWN / MULTISELECT only) --- */}
                    { hasOptions && (
                        <Form.Field>
                            <Checkbox
                                label="Allow respondent to provide additional context (follow-up text)"
                                checked={ draft.hasFollowUpText }
                                onChange={ ( _, d ) => setField( 'hasFollowUpText', !!d.checked ) }
                            />
                        </Form.Field>
                    ) }

                    {/* --- Required --- */}
                    <Form.Field>
                        <Checkbox
                            label="Required"
                            checked={ draft.isRequired }
                            onChange={ ( _, d ) => setField( 'isRequired', !!d.checked ) }
                        />
                    </Form.Field>

                </Form>
            </Modal.Content>

            <Modal.Actions>
                <Button type="button" basic onClick={ onClose }>Cancel</Button>
                <Button
                    type="button"
                    color="purple"
                    disabled={ !draft.questionText.trim() }
                    onClick={ () => onSave( draft ) }>
                    Save Question
                </Button>
            </Modal.Actions>

        </Modal>
    );
}


// Hex values that correspond to Semantic UI's named palette colors
const CATEGORY_BORDER_HEX: Record<SurveyQuestionCategory, string> = {
    CORPORATE:     '#a333c8',
    MARKETING:     '#e03997',
    MANUFACTURING: '#b5cc18',
    SALES:         '#2185d0',
};


// --- Question Row ---

interface QuestionRowProps {
    question: SurveyQuestion;
    onEdit:   ( question: SurveyQuestion ) => void;
    onDelete: ( id: string ) => void;
}

function QuestionRow( { question, onEdit, onDelete }: QuestionRowProps ){

    const [ hovered, setHovered ] = useState( false );
    const typeBadge               = getTypeBadgeLabel( question );

    return (
        <div
            style={ {
                display:         'flex',
                alignItems:      'flex-start',
                gap:             '12px',
                padding:         '10px 12px 10px 14px',
                borderLeft:      `3px solid ${ CATEGORY_BORDER_HEX[ question.category ] }`,
                borderBottom:    '1px solid #f0f0f0',
                cursor:          'pointer',
                background:      hovered ? '#fafafa' : 'white',
                transition:      'background 0.12s',
            } }
            onClick={ () => onEdit( question ) }
            onMouseEnter={ () => setHovered( true ) }
            onMouseLeave={ () => setHovered( false ) }>

            <div style={ { flex: 1, minWidth: 0 } }>
                <div style={ { display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '5px', flexWrap: 'wrap' } }>
                    <Label size="tiny" color={ CATEGORY_COLOR[ question.category ] as any } style={ { margin: 0 } }>
                        { CATEGORY_LABEL[ question.category ].toUpperCase() }
                    </Label>
                    <Label size="tiny" basic style={ { margin: 0 } }>
                        { typeBadge }
                    </Label>
                    { question.isRequired && (
                        <Label size="tiny" basic color="red" style={ { margin: 0 } }>REQUIRED</Label>
                    ) }
                </div>
                <div style={ { fontSize: '0.875em', color: '#333', lineHeight: 1.45, wordBreak: 'break-word' } }>
                    { question.questionText }
                </div>
            </div>

            <div
                style={ { display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0, paddingTop: '2px' } }
                onClick={ e => e.stopPropagation() }>
                <Button
                    basic
                    size="mini"
                    color="teal"
                    type="button"
                    onClick={ () => onEdit( question ) }>
                    Open
                </Button>
                <Icon
                    name="trash alternate outline"
                    link
                    style={ { color: '#ccc' } }
                    onClick={ () => onDelete( question.id ) }
                />
            </div>

        </div>
    );
}


// --- Main Component ---

type ActiveCategory = SurveyQuestionCategory | 'ALL';

interface SurveyBuilderSectionProps {}

function SurveyBuilderSection( _props: SurveyBuilderSectionProps ){

    const { RunInsert, RunUpdate, RunDelete, TableSelect } = useCentralCalls();

    const [ questions, setQuestions ]             = useState<SurveyQuestion[]>( [] );
    const [ searchQuery, setSearchQuery ]         = useState( '' );
    const [ activeCategory, setActiveCategory ]   = useState<ActiveCategory>( 'ALL' );
    const [ editingQuestion, setEditingQuestion ] = useState<SurveyQuestion | null>( null );
    const [ isModalOpen, setIsModalOpen ]         = useState( false );
    const [ isLoading, setIsLoading ]             = useState( true );
    const [ isSaving, setIsSaving ]               = useState( false );

    // Load all questions plus their options from the database.
    const loadQuestions = async () => {
        setIsLoading( true );
        try {
            const questionRows: SurveyQuestionRow[] = await TableSelect( SURVEY_QUESTION_TABLE, {} ) ?? [];

            const loaded = await Promise.all(
                questionRows.map( async ( row ) => {
                    const optionRows: SurveyQuestionOptionRow[] =
                        await TableSelect( SURVEY_QUESTION_OPTION_TABLE, { SurveyQuestionId: row.id } ) ?? [];
                    return toLocalQuestion( row, optionRows );
                } ),
            );

            setQuestions( loaded );
        } catch( err ){
            console.error( '[SurveyBuilder] failed to load questions:', err );
        } finally {
            setIsLoading( false );
        }
    };

    useEffect( () => {
        loadQuestions();
    }, [] );

    // Remove every option row belonging to a question. Used before re-inserting on edit and before delete.
    const deleteOptionsForQuestion = async ( questionId: string ) => {
        const existingOptions: SurveyQuestionOptionRow[] =
            await TableSelect( SURVEY_QUESTION_OPTION_TABLE, { SurveyQuestionId: questionId } ) ?? [];

        for( const option of existingOptions ){
            await RunDelete( SURVEY_QUESTION_OPTION_TABLE, option.id );
        }
    };

    // Insert one option row per non-empty option string.
    const insertOptionsForQuestion = async ( questionId: string, options: string[] ) => {
        for( const optionText of options ){
            await RunInsert( SURVEY_QUESTION_OPTION_TABLE, {
                Id:               uuid(),
                SurveyQuestionId: questionId,
                OptionText:       optionText,
            } );
        }
    };

    const CATEGORY_TABS: ActiveCategory[] = [ 'ALL', 'CORPORATE', 'MARKETING', 'MANUFACTURING', 'SALES' ];

    const categoryCounts = _.countBy( questions, q => q.category ) as Partial<Record<SurveyQuestionCategory, number>>;

    const filteredQuestions = questions.filter( q => {
        const matchesCategory = activeCategory === 'ALL' || q.category === activeCategory;
        const matchesSearch   = !searchQuery || q.questionText.toLowerCase().includes( searchQuery.toLowerCase() );
        return matchesCategory && matchesSearch;
    } );

    const handleAddQuestion = () => {
        setEditingQuestion( null );
        setIsModalOpen( true );
    };

    const handleEditQuestion = ( question: SurveyQuestion ) => {
        setEditingQuestion( question );
        setIsModalOpen( true );
    };

    const handleSaveQuestion = async ( question: SurveyQuestion ) => {
        const isExisting  = questions.some( q => q.id === question.id );
        const cleanOptions = question.options.filter( o => o.trim() !== '' );

        // Payload keys are the DB column names (PascalCase); values come from the UI model.
        const columns = {
            QuestionType: question.type,
            Question:     question.questionText,
            Description:  question.description || null,
        };

        setIsSaving( true );
        try {
            if( isExisting ){
                await RunUpdate( SURVEY_QUESTION_TABLE, question.id, columns );
                // Options have no stable identity in the UI, so replace the whole set.
                await deleteOptionsForQuestion( question.id );
                await insertOptionsForQuestion( question.id, cleanOptions );

                setQuestions( prev => prev.map( q => q.id === question.id ? question : q ) );
            } else {
                await RunInsert( SURVEY_QUESTION_TABLE, { Id: question.id, ...columns } );
                await insertOptionsForQuestion( question.id, cleanOptions );

                setQuestions( prev => [ ...prev, question ] );
            }

            setIsModalOpen( false );
            setEditingQuestion( null );
        } catch( err ){
            console.error( '[SurveyBuilder] failed to save question:', err );
        } finally {
            setIsSaving( false );
        }
    };

    const handleDeleteQuestion = async ( id: string ) => {
        setIsSaving( true );
        try {
            await deleteOptionsForQuestion( id );
            await RunDelete( SURVEY_QUESTION_TABLE, id );

            setQuestions( prev => prev.filter( q => q.id !== id ) );
        } catch( err ){
            console.error( '[SurveyBuilder] failed to delete question:', err );
        } finally {
            setIsSaving( false );
        }
    };

    const requiredCount = questions.filter( q => q.isRequired ).length;

    if( isLoading ){
        return <LoadingIndicator text="Loading questions..."/>;
    }

    return (
        <>
            <EditQuestionModal
                open={ isModalOpen }
                question={ editingQuestion }
                onClose={ () => setIsModalOpen( false ) }
                onSave={ handleSaveQuestion }
            />

            <div style={ { display: 'flex', flexDirection: 'column', height: '100%' } }>

                {/* ── Row 1: inline stats + create button ── */}
                <div style={ {
                    display:       'flex',
                    alignItems:    'center',
                    justifyContent: 'space-between',
                    marginBottom:  '10px',
                    paddingBottom: '10px',
                    borderBottom:  '1px solid #ebebeb',
                } }>
                    <div style={ { display: 'flex', alignItems: 'center', gap: '0' } }>
                        { [
                            { value: questions.length, label: 'Questions' },
                            { value: Object.keys( categoryCounts ).length, label: 'Categories' },
                            { value: requiredCount, label: 'Required' },
                        ].map( ( stat, i ) => (
                            <div
                                key={ stat.label }
                                style={ {
                                    paddingRight: i < 2 ? '16px' : 0,
                                    marginRight:  i < 2 ? '16px' : 0,
                                    borderRight:  i < 2 ? '1px solid #e0e0e0' : 'none',
                                } }>
                                <span style={ { fontSize: '1.25em', fontWeight: 700, color: '#333' } }>
                                    { stat.value }
                                </span>
                                <span style={ { fontSize: '0.78em', color: '#aaa', marginLeft: '5px' } }>
                                    { stat.label }
                                </span>
                            </div>
                        ) ) }
                    </div>

                    <Button color="purple" size="small" type="button" disabled={ isSaving } onClick={ handleAddQuestion }>
                        <Icon name="plus"/> Create question
                    </Button>
                </div>

                {/* ── Row 2: category filters + search ── */}
                <div style={ {
                    display:     'flex',
                    alignItems:  'center',
                    gap:         '8px',
                    marginBottom: '10px',
                } }>
                    <div style={ { display: 'flex', gap: '4px', flex: 1, flexWrap: 'wrap', alignItems: 'center' } }>
                        { CATEGORY_TABS.map( cat => {
                            const count    = cat === 'ALL' ? questions.length : ( categoryCounts[ cat as SurveyQuestionCategory ] ?? 0 );
                            const isActive = activeCategory === cat;
                            return (
                                <Label
                                    key={ cat }
                                    as="a"
                                    color={ isActive ? ( cat === 'ALL' ? 'grey' : ( CATEGORY_COLOR[ cat as SurveyQuestionCategory ] as any ) ) : undefined }
                                    basic={ !isActive }
                                    style={ { cursor: 'pointer', margin: 0 } }
                                    onClick={ () => setActiveCategory( cat ) }>
                                    { cat === 'ALL' ? 'All' : CATEGORY_LABEL[ cat as SurveyQuestionCategory ] }
                                    <Label.Detail>{ count }</Label.Detail>
                                </Label>
                            );
                        } ) }
                    </div>

                    <Input
                        size="small"
                        icon="search"
                        iconPosition="left"
                        placeholder="Search..."
                        value={ searchQuery }
                        onChange={ e => setSearchQuery( e.target.value ) }
                        style={ { width: '150px', flexShrink: 0 } }
                    />
                </div>

                {/* ── Question list ── */}
                <div style={ {
                    flex:       1,
                    overflowY:  'auto',
                    border:     '1px solid #ebebeb',
                    borderRadius: '4px',
                    background: 'white',
                } }>
                    { filteredQuestions.length === 0 ? (
                        <EmptyCollectionMessage
                            icon="question circle outline"
                            header="No questions found"
                            content="Add a question using the button above, or adjust your filters."
                        />
                    ) : (
                          filteredQuestions.map( question => (
                              <QuestionRow
                                  key={ question.id }
                                  question={ question }
                                  onEdit={ handleEditQuestion }
                                  onDelete={ handleDeleteQuestion }
                              />
                          ) )
                      ) }
                </div>

                {/* ── Admin note ── */}
                <div style={ { paddingTop: '8px' } }>
                    <FinePrintMessage>
                        <Icon name="info circle"/> Admin mode — you are designing this questionnaire. User responses will influence template recommendations on the next step.
                    </FinePrintMessage>
                </div>

            </div>
        </>
    );
}


export {
    SurveyBuilderSection,
    type SurveyBuilderSectionProps,
};
